import React, { useEffect, useState } from "react";
import { Shield, Sparkles, Check } from "lucide-react";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function SafetyPlans() {
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ client_id: "", warning_signs: "", coping_strategies: "", social_supports: "", professional_contacts: "", means_restriction: "" });
  const [gen, setGen] = useState(false);
  const [active, setActive] = useState(null);

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    api.get("/safety-plans").then(({ data }) => setPlans(data)),
  ]);
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.client_id) return toast.error("Select a client");
    setGen(true);
    try {
      const { data } = await api.post("/safety-plans/generate", form);
      setPlans([data, ...plans]);
      setActive(data);
      toast.success("Safety plan generated");
    } catch { toast.error("Generation failed"); }
    setGen(false);
  };

  const sign = async (id) => {
    await api.put(`/safety-plans/${id}/sign`);
    toast.success("Signed");
    load();
    if (active?.id === id) setActive({ ...active, signed: true });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page title="Safety Plans" subtitle="Stanley-Brown Safety Planning Intervention. AI-prefilled. Co-signed by client.">
      <div className="grid lg:grid-cols-2 gap-5">
        <Card delay={0.05} testid="safety-composer">
          <h2 className="font-serif text-2xl mb-4">New Safety Plan</h2>
          <div className="space-y-3">
            <select value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-3 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="safety-client-select">
              <option value="">Select client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
            <textarea placeholder="1. Warning signs" value={form.warning_signs} onChange={set("warning_signs")} rows={2} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="safety-warning"></textarea>
            <textarea placeholder="2. Internal coping strategies" value={form.coping_strategies} onChange={set("coping_strategies")} rows={2} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="safety-coping"></textarea>
            <textarea placeholder="3. Social supports" value={form.social_supports} onChange={set("social_supports")} rows={2} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="safety-social"></textarea>
            <textarea placeholder="4. Professional contacts" value={form.professional_contacts} onChange={set("professional_contacts")} rows={2} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="safety-professional"></textarea>
            <textarea placeholder="5. Means restriction" value={form.means_restriction} onChange={set("means_restriction")} rows={2} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none" style={{ background: "var(--mf-muted)" }} data-testid="safety-means"></textarea>
            <PrimaryButton onClick={generate} disabled={gen} testid="safety-generate-btn">
              {gen ? "Drafting…" : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Generate with AI</span>}
            </PrimaryButton>
          </div>
        </Card>

        <Card delay={0.1} testid="safety-preview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">Plan Preview</h2>
            {active && !active.signed && <PrimaryButton onClick={() => sign(active.id)} testid="safety-sign-btn"><span className="flex items-center gap-2"><Check className="w-4 h-4" /> Client Co-sign</span></PrimaryButton>}
            {active?.signed && <Pill variant="success">Signed</Pill>}
          </div>
          {active ? (
            <div className="space-y-2">
              <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Review due {new Date(active.review_due).toLocaleDateString()}</div>
              <div className="p-4 rounded-xl text-sm whitespace-pre-wrap mf-prose" style={{ background: "var(--mf-muted)" }}>{active.ai_suggestions}</div>
            </div>
          ) : (
            <div className="text-sm py-12 text-center" style={{ color: "var(--mf-text-muted)" }}>Generate a plan to preview.</div>
          )}
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">All Safety Plans</h2>
        {plans.length === 0 ? <EmptyState icon={Shield} title="No safety plans yet" description="Build your first Stanley-Brown safety plan." /> : (
          <div className="grid md:grid-cols-2 gap-4">
            {plans.map((p, i) => {
              const client = clients.find(c => c.id === p.client_id);
              return (
                <div key={p.id} className="mf-card p-5 cursor-pointer mf-fade-in" style={{ animationDelay: `${Math.min(i*0.05, 0.3)}s` }} onClick={() => setActive(p)} data-testid={`safety-item-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">{client ? `${client.first_name} ${client.last_name}` : "—"}</div>
                    <Pill variant={p.signed ? "success" : "warn"}>{p.signed ? "Signed" : "Unsigned"}</Pill>
                  </div>
                  <div className="text-xs mb-2" style={{ color: "var(--mf-text-muted)" }}>Review due {new Date(p.review_due).toLocaleDateString()}</div>
                  <div className="text-sm line-clamp-3">{p.ai_suggestions?.slice(0, 200)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Page>
  );
}
